import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { BrandingProvider } from "./contexts/BrandingContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import SupportWidget from "./components/SupportWidget";
import AIChatbot from "./components/AIChatbot";

// Pages
import Home from "./pages/Home";
import Treatments from "./pages/Treatments";
import Memberships from "./pages/Memberships";
import Booking from "./pages/Booking";
import Help from "./pages/Help";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PaymentSuccess from "./pages/PaymentSuccess";
import Shop from "./pages/Shop";
import GiftCards from "./pages/GiftCards";

// Admin Pages
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAppointments from "./pages/admin/AdminAppointments";
import AdminPatients from "./pages/admin/AdminPatients";
import AdminMemberships from "./pages/admin/AdminMemberships";
import AdminMarketing from "./pages/admin/AdminMarketing";
import AdminSMS from "./pages/admin/AdminSMS";
import AdminReports from "./pages/admin/AdminReports";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminTreatments from "./pages/admin/AdminTreatments";
import Settings from "./pages/admin/Settings";
import PracticeSignup from "./pages/admin/PracticeSignup";
import DataImport from "./pages/admin/DataImport";
import HelpCenter from "./pages/admin/HelpCenter";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminPaymentProcessing from "./pages/admin/AdminPaymentProcessing";
import AdminPermissions from "./pages/admin/AdminPermissions";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminFAQs from "./pages/admin/AdminFAQs";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AppBuilder from "./pages/admin/AppBuilder";
import MembershipsReport from "./pages/admin/MembershipsReport";
import ServicesReport from "./pages/admin/ServicesReport";
import AdminRewards from "./pages/admin/AdminRewards";
import AdminAutomatedOffers from "./pages/admin/AdminAutomatedOffers";
import AdminSpecialOffers from "./pages/admin/AdminSpecialOffers";
import AdminAIAdvisors from "./pages/admin/AdminAIAdvisors";
import AdminGiftCards from "./pages/admin/AdminGiftCards";
import AdminEmailTemplatesPage from "./pages/admin/AdminEmailTemplatesPage";
import AdminTransactions from "./pages/admin/AdminTransactions";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      
      {/* Practice Signup */}
      <Route path="/signup" element={<PracticeSignup />} />
      
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/treatments" element={<Treatments />} />
      <Route path="/memberships" element={<Memberships />} />
      <Route path="/booking" element={<Booking />} />
      <Route path="/help" element={<Help />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/gift-cards" element={<GiftCards />} />
      
      {/* Admin Routes - Protected */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="appointments" element={<AdminAppointments />} />
        <Route path="patients" element={<AdminPatients />} />
        <Route path="memberships" element={<AdminMemberships />} />
        <Route path="treatments" element={<AdminTreatments />} />
        <Route path="marketing" element={<AdminMarketing />} />
        <Route path="sms" element={<AdminSMS />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="staff" element={<AdminStaff />} />
        <Route path="settings" element={<Settings />} />
        <Route path="import" element={<DataImport />} />
        <Route path="help" element={<HelpCenter />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="payment-processing" element={<AdminPaymentProcessing />} />
        <Route path="permissions" element={<AdminPermissions />} />
        <Route path="referrals" element={<AdminReferrals />} />
        <Route path="faqs" element={<AdminFAQs />} />
        <Route path="email-templates" element={<AdminEmailTemplates />} />
        <Route path="app-builder" element={<AppBuilder />} />
        <Route path="reports/memberships" element={<MembershipsReport />} />
        <Route path="reports/services" element={<ServicesReport />} />
        <Route path="rewards" element={<AdminRewards />} />
        <Route path="automated-offers" element={<AdminAutomatedOffers />} />
        <Route path="special-offers" element={<AdminSpecialOffers />} />
        <Route path="ai-advisors" element={<AdminAIAdvisors />} />
        <Route path="giftcards" element={<AdminGiftCards />} />
        <Route path="email-templates-editor" element={<AdminEmailTemplatesPage />} />
        <Route path="transactions" element={<AdminTransactions />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <AppRoutes />
            <SupportWidget />
            <AIChatbot />
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
